import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { sessionStore } from "./store";
import { IAccountContext } from "./types";
import { Center, Loader } from "@mantine/core";
import { useRouter } from "next/router";
import {
  persistUTMQueryParamsToString,
  redirectAfterLogout,
  safeRedirectPath,
} from "@common/utils/router";
import { paymentMethodsStore } from "@stores/payment-methods";
import { isProPlan, isSelfHostedPlan } from "@common/utils/plan";
import { PARENT_PLAN_PRO_TYPE } from "@stores/plans/constants";

export const AccountContext = createContext<IAccountContext>({});

export function AccountProvider({ children }: PropsWithChildren): ReactElement {
  const router = useRouter();

  // payment
  const {
    isLoading: loadingDefaultPaymentMethod,
    refetch: refetchDefaultPaymentMethod,
    data: defaultPaymentMethod,
  } = paymentMethodsStore.useDefaultPaymentMethod({ enabled: false });

  // account
  const {
    isLoading: loadingAccount,
    error: accountError,
    refetch: refetchAccount,
    data: account,
  } = sessionStore.useGetAccount({
    enabled: true,
    refetchInterval: 1000 * 60 * 5, // reload every 5 minutes
  });

  const shouldLogoutCustomer = useMemo(
    () => !loadingAccount && !!account && accountError?.status === 404,
    [loadingAccount, account, accountError],
  );

  // first account loading
  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    async function getDetailsAsync() {
      await refetchAccount();
      await refetchDefaultPaymentMethod();

      setFirstLoading(false);
    }

    getDetailsAsync();
  }, [refetchAccount, refetchDefaultPaymentMethod]);

  // utils
  const consolidatedLoading = loadingAccount || loadingDefaultPaymentMethod;

  // redirects
  useEffect(() => {
    const { pathname, query } = router;

    // Redirecting because
    // the customer has been logged out
    // in the background
    if (shouldLogoutCustomer) {
      redirectAfterLogout(router, true);
    }
    // Customer is logged out
    else if (!consolidatedLoading) {
      const isLanding = pathname === "/";
      const isCheckout =
        pathname.startsWith("/checkout") && pathname !== "/checkout/upgrade"; // Upgrade checkout has a different way to redirect
      const isLogin = pathname.startsWith("/login");
      const isAccount = pathname.startsWith("/account");
      const isSnapshots = pathname.startsWith("/snapshots/");
      if (isLanding || isCheckout || isLogin || isAccount || isSnapshots) {
        const isLoggedIn = !!account;

        // 0. migration redirect
        // from pro-self-hosted(-yearly) to cloud
        const {
          "migration-source-plan": migrationSourcePlan,
          "migration-id": migrationId,
        } = query;
        if (
          isLoggedIn &&
          isLogin &&
          isProPlan(String(migrationSourcePlan)) &&
          isSelfHostedPlan(String(migrationSourcePlan)) &&
          !!migrationId
        ) {
          router.push(
            persistUTMQueryParamsToString(router, "/account/new-instance", {
              "migration-source-plan": migrationSourcePlan,
              "migration-id": migrationId,
              plan: PARENT_PLAN_PRO_TYPE,
            }),
          );
        }
        // 1. login
        // redirect to /account
        // redirectTo query parameter
        else if (isLoggedIn && (isLanding || isLogin)) {
          const { redirectTo, ...queryParams } = query;
          router.push(
            persistUTMQueryParamsToString(
              router,
              safeRedirectPath(redirectTo) ?? "/account",
              queryParams,
            ),
          );
        }
        // 2. landing
        // redirect logged-out customers to checkout after the API-backed
        // session check completes.
        //
        // /checkout is served by a separate microfrontend, so we use a hard
        // navigation instead of router.push. A client-side push makes Next try
        // to resolve/prefetch /checkout as an internal route, which fails with
        // "Failed to lookup route: /checkout" and aborts in-flight requests.
        else if (!isLoggedIn && isLanding) {
          window.location.assign(
            persistUTMQueryParamsToString(router, "/checkout"),
          );
        }
        // 3. checkout
        // redirect to /account/new-instance
        else if (isLoggedIn && defaultPaymentMethod && isCheckout) {
          const { redirectTo, ...queryParams } = query;
          router.push(
            persistUTMQueryParamsToString(
              router,
              safeRedirectPath(redirectTo) ?? "/account/new-instance",
              queryParams,
            ),
          );
        }
        // 4. logout
        // redirect to /login
        else if (!isLanding && !isCheckout && !isLogin && !isLoggedIn) {
          redirectAfterLogout(router);
        }
      }
    }
  }, [
    shouldLogoutCustomer,
    consolidatedLoading,
    router,
    account,
    defaultPaymentMethod,
  ]);

  if (consolidatedLoading && firstLoading) {
    return (
      <Center w="100%" h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <AccountContext.Provider value={{ account }}>
      {children}
    </AccountContext.Provider>
  );
}
